import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, ReplaySubject } from '../util/rxjs';

import { CallOptions, HttpService } from '../http.service';
import { LoginRequest, PasswordChange, PmInfo, User, convertContactRole } from './user.model';
import { map } from 'rxjs/operators';

@Injectable()
export class UserService {
  user$: Observable<User>;
  private userStream = new ReplaySubject<User>(1);

  constructor(private http: HttpService, router: Router) {
    this.http.authorizationErrors$.subscribe(() => {
      this.setAnonymous();
      router.navigate(['login'], { queryParams: { url: encodeURIComponent(router.url) } });
    });
    this.http.roleErrors$.subscribe(() => this.updateRole());

    this.user$ = this.userStream.asObservable();
  }

  update(user: User) {
    this.userStream.next(user);
  }

  setFromProfile(profile: accounts.UserProfile) {
    const user = new User(
      profile.IsAuthenticated,
      profile.Name,
      profile.Email,
      convertContactRole(profile.Role)
    );
    this.update(user);
  }

  setAnonymous() {
    this.update(new User(false));
  }

  login(request: LoginRequest) {
    return this.http.post<accounts.UserProfile, accounts.LoginRequest>(
      'account/login',
      {
        EMail: request.email,
        Password: request.password
      },
      {
        redirectOnUnauthorized: false,
        emitGlobalError: false,
        rethrowError: true
      }
    );
  }

  confirmLogin() {
    return this.http.post<accounts.UserProfile, {}>(
      'account/login/confirm',
      {},
      {
        redirectOnUnauthorized: false,
        emitGlobalError: false,
        rethrowError: true
      }
    );
  }

  logout() {
    return this.http.post(
      'account/logout',
      {},
      {
        rethrowError: true
      }
    );
  }

  logoutWithOptions(options?: CallOptions) {
    return this.http.post<accounts.UserProfile, {}>('account/logout', {}, options);
  }

  updateRole() {
    Observable.race(
      this.user$
        .take(1)
        .zip(this.http.get<accounts.ContactRole>('account/me/role', { emitRoleError: false }))
        .map(
          ([user, role]) =>
            new User(user.isAuthenticated, user.name, user.email, convertContactRole(role))
        ),
      this.user$.skip(1).take(1)
    ).subscribe(user => this.userStream.next(user));
  }

  getCurrentUser() {
    return this.http.get<accounts.UserProfile>('account/me', {
      rethrowError: true
    });
  }

  listPMs() {
    return this.http
      .get<accounts.PmInfo[]>('account/me/pm')
      .pipe(
        map(infos => infos.map(info => new PmInfo(info)))
      );   
  }

  changePassword(request: PasswordChange) {
    return this.http.post<accounts.PasswordChangeData, accounts.PasswordChangeData>(
      'account/change',
      {
        Email: request.email,
        NewPassword: request.newPassword,
        OldPassword: request.oldPassword
      }
    );
  }

  getOidcAuthParameters() {
    return this.http.get<accounts.OidcAuthParameters>(
      'account/oidc-auth-parameters',
      {
        redirectOnUnauthorized: false,
        emitGlobalError: false,
        rethrowError: true
      }
    );
  }

  oidcLogin(loginId: string) {
    return this.http.post<{}, accounts.OidcLoginParameters>(
      'account/oidc-login',
      {
        LoginId: loginId,
      },
      {
        redirectOnUnauthorized: false,
        emitGlobalError: false,
        rethrowError: true
      }
    )
  }
}
